import { AddIcon } from './icons';
import { surfaceShadows } from '../../surfaceStyles';
import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { Box, Button, Paper } from '@mui/material';
import { MobileNavigation, MobileNavigationProps } from './MobileNavigation';
import { MobileFooterGeometry } from './mobileFooterGeometry';

export type MobilePrimaryAction = { label: string; onClick: () => void };

export function MobileFooter({
  action,
  showNavigation = true,
  ...navigation
}: MobileNavigationProps & {
  action?: MobilePrimaryAction;
  /** False where there is only one screen, e.g. the demo. */
  showNavigation?: boolean;
}) {
  const footerRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLDivElement>(null);
  const hasAction = Boolean(action);
  const [height, setHeight] = useState(0);
  const { setNavigationHeight } = useContext(MobileFooterGeometry);
  useLayoutEffect(() => {
    const footer = footerRef.current;
    const nav = navigationRef.current;
    if (!footer || (showNavigation && !nav)) return;
    const measure = () => {
      setHeight(footer.getBoundingClientRect().height);
      if (nav) setNavigationHeight(nav.getBoundingClientRect().height);
    };
    measure();
    const observer =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(measure);
    observer?.observe(footer);
    if (nav) observer?.observe(nav);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [setNavigationHeight, hasAction, showNavigation]);
  return (
    <>
      <Box aria-hidden data-testid="mobile-footer-clearance" sx={{ height }} />
      <Paper
        ref={footerRef}
        data-testid="mobile-footer"
        square
        elevation={0}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          pb: 'env(safe-area-inset-bottom)',
          bgcolor: 'background.paper',
          boxShadow: surfaceShadows.footer,
        }}
      >
        {action && (
          <Box sx={{ px: 1.5, py: 1 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<AddIcon />}
              onClick={action.onClick}
              sx={{ borderRadius: 1 }}
            >
              {action.label}
            </Button>
          </Box>
        )}
        {showNavigation && (
          <Box ref={navigationRef}>
            <MobileNavigation {...navigation} />
          </Box>
        )}
      </Paper>
    </>
  );
}
